import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  MapPin, Maximize2, Calendar, Building2, X,
  ChevronLeft, ChevronRight, Image as ImageIcon, StickyNote,
  SlidersHorizontal, ChevronDown, ChevronUp,
} from 'lucide-react';
import { getBuildings, fetchImage } from '../services/api';

interface BuildingImage { url: string; caption?: string; }
interface Building {
  id: number;
  name: string;
  type: string;
  location: string;
  status: string;
  area: string;
  completedYear: string;
  description: string;
  colorIndex: number;
  mainImageFsId?: string;
  imagesFsId?: BuildingImage[];
  notes?: string[];
}

const colors = [
  'linear-gradient(135deg,#e85d04,#fb8500)',
  'linear-gradient(135deg,#0077b6,#00b4d8)',
  'linear-gradient(135deg,#2d6a4f,#52b788)',
  'linear-gradient(135deg,#6a0572,#a629a6)',
  'linear-gradient(135deg,#1a1a6e,#023e8a)',
  'linear-gradient(135deg,#7b2d00,#c44d00)',
];

const TYPE_FILTERS = [
  'All', 'Road Construction', 'Road Rehabilitation', 'Commercial Building',
  'Office Building', 'Industrial Building', 'Car Park', 'Residential',
  'Lecture Theatre', 'Government Structure',
];

const PER_PAGE_OPTIONS = [6, 12, 24, 48];

// ── helpers ───────────────────────────────────────────────────────────────────
const toYear = (s?: string) => { const n = parseInt(s ?? '', 10); return isNaN(n) ? null : n; };

// ── Building Detail Modal ─────────────────────────────────────────────────────
interface ResolvedSlide { objectUrl: string | null; caption?: string; }

const BuildingDetailModal: React.FC<{ building: Building; onClose: () => void }> = ({ building, onClose }) => {
  const gradient = colors[building.colorIndex ?? 0];

  const rawSlides: BuildingImage[] = [
    ...(building.mainImageFsId ? [{ url: building.mainImageFsId, caption: 'Cover image' }] : []),
    ...(building.imagesFsId ?? []).map((id, i) => ({
      url: `${id}`, 
      caption: building.notes?.[i] ?? undefined
    })),
  ];

  const [imgIndex, setImgIndex] = useState(0);
  const [resolved, setResolved] = useState<Record<string, ResolvedSlide>>({});
  const [loading,  setLoading]  = useState(true);
  const objectUrls = useRef<string[]>([]);

  useEffect(() => {
    if (!rawSlides.length) { 
      setLoading(false); 
      return; 
    }

    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        rawSlides.map(async (slide) => {
          try {
            const res = await fetchImage(slide.url);
            const blobUrl = URL.createObjectURL(res.data);
            objectUrls.current.push(blobUrl);
            return [slide.url, { objectUrl: blobUrl, caption: slide.caption }] as const;
          } catch {
            return [slide.url, { objectUrl: null, caption: slide.caption }] as const;
          }
        })
      );

      if (!cancelled) {
        setResolved(Object.fromEntries(entries)); 
        setLoading(false); 
      }

    })();

    return () => { cancelled = true; objectUrls.current.forEach(u => URL.revokeObjectURL(u)); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [building.id]);

  const slides: ResolvedSlide[] = rawSlides.map(s => resolved[s.url] ?? { objectUrl: null, caption: s.caption });
  const total   = slides.length;
  const current = slides[imgIndex];

  const prev = () => setImgIndex(i => (i - 1 + total) % total);
  const next = () => setImgIndex(i => (i + 1) % total);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft'  && total > 1) prev();
      if (e.key === 'ArrowRight' && total > 1) next();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose, total]);

  return (
    <div
      style={{ position:'fixed', inset:0, zIndex:1000, background:'rgba(0,0,0,0.72)', display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background:'var(--surface,#fff)', borderRadius:16, width:'100%', maxWidth:780, maxHeight:'90vh', overflow:'hidden', display:'flex', flexDirection:'column', boxShadow:'0 24px 80px rgba(0,0,0,0.35)' }}
      >
        {/* Hero image */}
        <div style={{ position:'relative', flexShrink:0, height:340, background:gradient, overflow:'hidden' }}>
          {loading ? (
            <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:10 }}>
              <div style={{ width:32, height:32, border:'3px solid rgba(255,255,255,0.3)', borderTopColor:'#fff', borderRadius:'50%', animation:'spin 0.8s linear infinite' }}/>
              <span style={{ color:'rgba(255,255,255,0.7)', fontSize:'0.85rem' }}>Loading images…</span>
            </div>
          ) : total > 0 ? (
            <>
              {current.objectUrl
                ? <img key={imgIndex} src={current.objectUrl} alt={current.caption ?? building.name} style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }}/>
                : <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:8 }}>
                    <Building2 size={56} color="rgba(255,255,255,0.35)"/>
                    <span style={{ color:'rgba(255,255,255,0.5)', fontSize:'0.78rem' }}>Image unavailable</span>
                  </div>
              }
              {current.caption && current.caption !== 'Cover image' && (
                <div style={{ position:'absolute', bottom:0, left:0, right:0, padding:'20px 16px 10px', background:'linear-gradient(transparent,rgba(0,0,0,0.65))', color:'#100c0c', fontSize:'0.8rem', fontStyle:'italic' }}>
                  {current.caption}
                </div>
              )}

              {/* Other images are here */}
              {total > 1 && <>
                <button onClick={e => { e.stopPropagation(); prev(); }} style={arrowStyle('left')}><ChevronLeft size={20}/></button>
                <button onClick={e => { e.stopPropagation(); next(); }} style={arrowStyle('right')}><ChevronRight size={20}/></button>
                <div style={{ position:'absolute', bottom:10, left:'50%', transform:'translateX(-50%)', display:'flex', gap:5, zIndex:2 }}>
                  {slides.map((_, i) => (
                    <button key={i} onClick={e => { e.stopPropagation(); setImgIndex(i); }} style={{ width: i===imgIndex?18:8, height:8, borderRadius:4, border:'none', cursor:'pointer', padding:0, background: i===imgIndex?'#fff':'rgba(255,255,255,0.45)', transition:'all 0.2s' }}/>
                  ))}
                </div>
                <div style={{ position:'absolute', top:'50%', transform:'translateY(-50%)', right:10, display:'flex', flexDirection:'column', gap:5, zIndex:2 }}>
                  {slides.slice(0,5).map((slide, i) => (
                    <button key={i} onClick={e => { e.stopPropagation(); setImgIndex(i); }} style={{ width:44, height:36, borderRadius:5, overflow:'hidden', padding:0, border:'none', cursor:'pointer', outline: i===imgIndex?'2px solid #fff':'2px solid transparent', opacity: i===imgIndex?1:0.6, transition:'all 0.15s', background:gradient }}>
                      {slide.objectUrl && <img src={slide.objectUrl} alt="" style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }}/>}
                    </button>
                  ))}
                </div>
              </>}
              {total > 1 && (
                <span style={{ position:'absolute', top:14, left:'50%', transform:'translateX(-50%)', background:'rgba(0,0,0,0.45)', color:'#fff', fontSize:'0.72rem', fontWeight:700, padding:'3px 10px', borderRadius:10 }}>
                  {imgIndex+1} / {total}
                </span>
              )}
            </>
          ) : (
            <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center' }}><Building2 size={72} color="rgba(255,255,255,0.35)"/></div>
          )}
          <span className={`building-status status-${(building.status||'').toLowerCase()}`} style={{ position:'absolute', top:14, left:14 }}>
            {building.status==='COMPLETED'?'Completed':building.status==='AVAILABLE'?'Available':'Sublet'}
          </span>
          <button onClick={onClose} style={{ position:'absolute', top:12, right:12, width:32, height:32, borderRadius:'50%', background:'rgba(0,0,0,0.45)', border:'none', color:'#fff', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', zIndex:3 }}>
            <X size={16}/>
          </button>
        </div>

        {/* Body */}
        <div style={{ overflowY:'auto', padding:'24px 28px 28px', flex:1 }}>
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:'0.72rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.12em', color:'var(--brand-orange,#e85d04)', marginBottom:4 }}>{building.type}</div>
            <h2 style={{ fontFamily:'var(--font-display,sans-serif)', fontSize:'1.6rem', fontWeight:700, color:'var(--text-primary,#111)', margin:'0 0 8px' }}>{building.name}</h2>
            <div style={{ display:'flex', gap:16, flexWrap:'wrap', color:'var(--text-secondary,#555)', fontSize:'0.85rem' }}>
              <span style={{ display:'flex', alignItems:'center', gap:4 }}><MapPin size={13}/>{building.location}</span>
              <span style={{ display:'flex', alignItems:'center', gap:4 }}><Maximize2 size={13}/>{building.area}</span>
              <span style={{ display:'flex', alignItems:'center', gap:4 }}><Calendar size={13}/>{building.completedYear}</span>
            </div>
          </div>
          {building.description && <p style={{ fontSize:'0.9rem', color:'var(--text-secondary,#555)', lineHeight:1.75, marginBottom:20 }}>{building.description}</p>}
          {(building.notes??[]).filter(n=>n.trim()).length > 0 && (
            <div style={{ marginBottom:20 }}>
              <div style={{ display:'flex', alignItems:'center', gap:7, marginBottom:10 }}>
                <StickyNote size={15} color="var(--brand-orange,#e85d04)"/>
                <span style={{ fontSize:'0.75rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.1em', color:'var(--text-muted,#888)' }}>Project Notes</span>
              </div>
              <ul style={{ margin:0, padding:0, listStyle:'none', display:'flex', flexDirection:'column', gap:8 }}>
                {building.notes!.filter(n=>n.trim()).map((note,i) => (
                  <li key={i} style={{ display:'flex', gap:10, alignItems:'flex-start', background:'var(--surface-alt,#f8f8f8)', borderRadius:8, padding:'10px 14px', fontSize:'0.87rem', color:'var(--text-muted,#888)', lineHeight:1.6 }}>
                    <span style={{ color:'var(--brand-orange,#e85d04)', fontWeight:700, flexShrink:0, marginTop:1 }}>{String(i+1).padStart(2,'0')}</span>
                    {note}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {total > 0 && (
            <div style={{ display:'flex', alignItems:'center', gap:6, color:'var(--text-muted,#888)', fontSize:'0.78rem', marginTop:4 }}>
              <ImageIcon size={13}/>{total} image{total!==1?'s':''}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const arrowStyle = (side:'left'|'right'): React.CSSProperties => ({
  position:'absolute', top:'50%', [side]:10, transform:'translateY(-50%)',
  width:36, height:36, borderRadius:'50%', background:'rgba(0,0,0,0.4)',
  border:'none', color:'#fff', cursor:'pointer',
  display:'flex', alignItems:'center', justifyContent:'center', zIndex:2,
});

// ── Pagination component ──────────────────────────────────────────────────────
const Pagination: React.FC<{
  page: number; totalPages: number;
  onPrev: () => void; onNext: () => void; onPage: (p: number) => void;
}> = ({ page, totalPages, onPrev, onNext, onPage }) => {
  if (totalPages <= 1) return null;

  // Build visible page numbers: always show first, last, current ±1, with ellipsis
  const pages: (number | '…')[] = [];
  const add = new Set<number>();
  [1, totalPages, page - 1, page, page + 1].forEach(p => {
    if (p >= 1 && p <= totalPages) add.add(p);
  });
  const sorted = Array.from(add).sort((a, b) => a - b);
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) pages.push('…');
    pages.push(p);
  });

  const btnBase: React.CSSProperties = {
    minWidth:36, height:36, borderRadius:8, border:'1px solid var(--border,#ddd)',
    cursor:'pointer', fontSize:'0.83rem', fontWeight:600,
    display:'flex', alignItems:'center', justifyContent:'center',
    transition:'all 0.15s',
  };

  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:6, marginTop:40, flexWrap:'wrap' }}>
      <button onClick={onPrev} disabled={page===1}
        style={{ ...btnBase, background:'var(--surface)', color: page===1?'var(--text-muted)':'var(--text-primary)', opacity: page===1?0.4:1, padding:'0 10px', gap:4 }}>
        <ChevronLeft size={15}/> Prev
      </button>

      {pages.map((p, i) =>
        p === '…'
          ? <span key={`e${i}`} style={{ padding:'0 4px', color:'var(--text-muted)', fontSize:'0.9rem' }}>…</span>
          : <button key={p} onClick={() => onPage(p as number)}
              style={{ ...btnBase,
                background: p===page ? 'var(--brand-orange,#e85d04)' : 'var(--surface)',
                color:       p===page ? '#fff' : 'var(--text-primary)',
                border:      p===page ? '1px solid var(--brand-orange,#e85d04)' : '1px solid var(--border,#ddd)',
              }}>{p}</button>
      )}

      <button onClick={onNext} disabled={page===totalPages}
        style={{ ...btnBase, background:'var(--surface)', color: page===totalPages?'var(--text-muted)':'var(--text-primary)', opacity: page===totalPages?0.4:1, padding:'0 10px', gap:4 }}>
        Next <ChevronRight size={15}/>
      </button>
    </div>
  );
};

// ── BuildingCatalogue ─────────────────────────────────────────────────────────
const BuildingCatalogue: React.FC = () => {
  const [buildings,  setBuildings]  = useState<Building[]>([]);
  const [active,     setActive]     = useState('All');
  const [selected,   setSelected]   = useState<Building | null>(null);
  const [cardImages, setCardImages] = useState<Record<number, string>>({});
  const cardObjectUrls = useRef<string[]>([]);

  // ── Pagination state ────────────────────────────────────────────────────────
  const [page,    setPage]    = useState(1);
  const [perPage, setPerPage] = useState(12);

  // ── Year range filter state ─────────────────────────────────────────────────
  const [showFilters, setShowFilters] = useState(false);
  const [yearFrom,    setYearFrom]    = useState('');
  const [yearTo,      setYearTo]      = useState('');

  useEffect(() => {
    getBuildings()
      .then(r => { if (r.data?.length) setBuildings(r.data); })
      .catch(() => {});
    return () => { cardObjectUrls.current.forEach(u => URL.revokeObjectURL(u)); };
  }, []);

  // Fetch card main images
  useEffect(() => {
    buildings.forEach(b => {
      if (!b.mainImageFsId || cardImages[b.id]) return;
      fetchImage(b.mainImageFsId)
        .then(res => {
          const url = URL.createObjectURL(res.data);
          cardObjectUrls.current.push(url);
          setCardImages(prev => ({ ...prev, [b.id]: url }));
        })
        .catch(() => {});
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildings]);

  // ── Derive year bounds from data ────────────────────────────────────────────
  const { minYear, maxYear } = useMemo(() => {
    const years = buildings.map(b => toYear(b.completedYear)).filter((y): y is number => y !== null);
    return years.length
      ? { minYear: Math.min(...years), maxYear: Math.max(...years) }
      : { minYear: 2000, maxYear: new Date().getFullYear() };
  }, [buildings]);

  // ── Filtered + paginated data ───────────────────────────────────────────────
  const filtered = useMemo(() => {
    const fromY = toYear(yearFrom);
    const toY   = toYear(yearTo);
    return buildings.filter(b => {
      if (active !== 'All' && b.type !== active) return false;
      const y = toYear(b.completedYear);
      if (fromY !== null && (y === null || y < fromY)) return false;
      if (toY   !== null && (y === null || y > toY))   return false;
      return true;
    });
  }, [buildings, active, yearFrom, yearTo]);

  const totalPages  = Math.max(1, Math.ceil(filtered.length / perPage));
  const safePage    = Math.min(page, totalPages);
  const pageStart   = (safePage - 1) * perPage;
  const paginated   = filtered.slice(pageStart, pageStart + perPage);

  // Reset to page 1 whenever filters change
  useEffect(() => { setPage(1); }, [active, yearFrom, yearTo, perPage]);

  const hasActiveFilters = yearFrom !== '' || yearTo !== '';

  const clearFilters = () => { setYearFrom(''); setYearTo(''); };

  return (
    <section className="section-py" id="catalogue">
      <div className="container">
        <div className="section-label">Portfolio</div>
        <h2 className="section-title">Completed Building Catalogue</h2>
        <p style={{ color:'var(--text-secondary)', maxWidth:'560px', lineHeight:'1.75', marginBottom:'32px' }}>
          A showcase of our completed infrastructure projects across Nigeria — each a testament to our engineering precision and commitment to quality.
        </p>

        {/* ── Type filter pills ── */}
        <div style={{ display:'flex', gap:'10px', flexWrap:'wrap', marginBottom:'16px' }}>
          {TYPE_FILTERS.map(f => (
            <button key={f} className={`filter-btn${active===f?' active':''}`} onClick={() => setActive(f)}>{f}</button>
          ))}
        </div>

        {/* ── Toolbar: advanced filters + per-page ── */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10, marginBottom: showFilters ? 0 : '28px' }}>
          {/* Left: toggle advanced filters */}
          <button
            onClick={() => setShowFilters(v => !v)}
            style={{
              display:'flex', alignItems:'center', gap:7, padding:'7px 14px', borderRadius:20,
              border: hasActiveFilters ? '1.5px solid var(--brand-orange,#e85d04)' : '1.5px solid var(--border,#ddd)',
              background: hasActiveFilters ? 'rgba(232,93,4,0.07)' : 'var(--surface)',
              color: hasActiveFilters ? 'var(--brand-orange,#e85d04)' : 'var(--text-secondary)',
              cursor:'pointer', fontSize:'0.82rem', fontWeight:600, transition:'all 0.15s',
            }}
          >
            <SlidersHorizontal size={14}/>
            Filters
            {hasActiveFilters && (
              <span style={{ background:'var(--brand-orange,#e85d04)', color:'#fff', borderRadius:10, fontSize:'0.68rem', fontWeight:700, padding:'1px 6px' }}>
                active
              </span>
            )}
            {showFilters ? <ChevronUp size={13}/> : <ChevronDown size={13}/>}
          </button>

          {/* Right: results count + per-page selector */}
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <span style={{ fontSize:'0.82rem', color:'var(--text-muted)', whiteSpace:'nowrap' }}>
              {filtered.length} building{filtered.length!==1?'s':''}
              {filtered.length !== buildings.length && ` of ${buildings.length}`}
            </span>
            <div style={{ display:'flex', alignItems:'center', gap:6 }}>
              <span style={{ fontSize:'0.8rem', color:'var(--text-muted)', whiteSpace:'nowrap' }}>Show</span>
              <select
                value={perPage}
                onChange={e => setPerPage(Number(e.target.value))}
                style={{
                  padding:'5px 10px', borderRadius:8, fontSize:'0.82rem', fontWeight:600,
                  border:'1.5px solid var(--border,#ddd)', background:'var(--surface)',
                  color:'var(--text-primary)', cursor:'pointer',
                }}
              >
                {PER_PAGE_OPTIONS.map(n => <option key={n} value={n}>{n} per page</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* ── Advanced filter panel (year range) ── */}
        {showFilters && (
          <div style={{
            margin:'12px 0 28px', padding:'18px 20px', borderRadius:12,
            border:'1px solid var(--border,#ddd)', background:'var(--surface-alt,#f8f8f8)',
            display:'flex', alignItems:'flex-end', gap:16, flexWrap:'wrap',
          }}>
            <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
              <label style={{ fontSize:'0.75rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', color:'var(--text-muted)' }}>
                Year From
              </label>
              <input
                type="number"
                value={yearFrom}
                min={minYear}
                max={maxYear}
                placeholder={String(minYear)}
                onChange={e => setYearFrom(e.target.value)}
                style={{
                  width:100, padding:'7px 10px', borderRadius:8, fontSize:'0.85rem',
                  border:'1.5px solid var(--border,#ddd)', background:'var(--surface)',
                  color:'var(--text-primary)',
                }}
              />
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
              <label style={{ fontSize:'0.75rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', color:'var(--text-muted)' }}>
                Year To
              </label>
              <input
                type="number"
                value={yearTo}
                min={minYear}
                max={maxYear}
                placeholder={String(maxYear)}
                onChange={e => setYearTo(e.target.value)}
                style={{
                  width:100, padding:'7px 10px', borderRadius:8, fontSize:'0.85rem',
                  border:'1.5px solid var(--border,#ddd)', background:'var(--surface)',
                  color:'var(--text-primary)',
                }}
              />
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:6, paddingBottom:1 }}>
              {/* Quick-range presets */}
              {[
                { label: 'Last 5 yrs',  from: maxYear - 4, to: maxYear },
                { label: 'Last 10 yrs', from: maxYear - 9, to: maxYear },
                { label: `${maxYear}`,  from: maxYear,     to: maxYear },
              ].map(preset => (
                <button
                  key={preset.label}
                  onClick={() => { setYearFrom(String(preset.from)); setYearTo(String(preset.to)); }}
                  style={{
                    padding:'6px 11px', borderRadius:8, fontSize:'0.78rem', fontWeight:600, cursor:'pointer',
                    border:'1.5px solid var(--border,#ddd)', background:'var(--surface)',
                    color:'var(--text-secondary)', transition:'all 0.15s',
                  }}
                >
                  {preset.label}
                </button>
              ))}
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  style={{
                    padding:'6px 11px', borderRadius:8, fontSize:'0.78rem', fontWeight:600, cursor:'pointer',
                    border:'1.5px solid rgba(220,53,69,0.35)', background:'rgba(220,53,69,0.06)',
                    color:'#dc3545', transition:'all 0.15s',
                  }}
                >
                  Clear
                </button>
              )}
            </div>
            {/* Active range summary */}
            {hasActiveFilters && (
              <div style={{ marginLeft:'auto', fontSize:'0.8rem', color:'var(--brand-orange,#e85d04)', fontWeight:600, alignSelf:'center' }}>
                Showing: {yearFrom || minYear} – {yearTo || maxYear}
              </div>
            )}
          </div>
        )}

        {/* ── Empty state ── */}
        {filtered.length === 0 && (
          <div style={{ textAlign:'center', padding:'64px 24px' }}>
            <Building2 size={48} color="var(--text-muted)" style={{ opacity:0.35, marginBottom:16 }}/>
            <p style={{ color:'var(--text-muted)', fontSize:'0.95rem', marginBottom:12 }}>
              No buildings match the current filters.
            </p>
            <button onClick={() => { setActive('All'); clearFilters(); }}
              style={{ padding:'8px 20px', borderRadius:8, border:'1px solid var(--border)', background:'var(--surface)', color:'var(--text-primary)', cursor:'pointer', fontWeight:600, fontSize:'0.85rem' }}>
              Reset all filters
            </button>
          </div>
        )}

        {/* ── Grid ── */}
        {paginated.length > 0 && (
          <div className="buildings-grid">
            {paginated.map((b, i) => {
              const gradient   = colors[b.colorIndex ?? i % colors.length];
              const cardImgUrl = cardImages[b.id];
              const totalImgs  = (b.imagesFsId?.length ?? 0) + (b.mainImageFsId ? 1 : 0);

              return (
                <div
                  key={b.id}
                  className="building-card"
                  onClick={() => setSelected(b)}
                  style={{ cursor:'pointer', transition:'transform 0.18s, box-shadow 0.18s' }}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-4px)';
                    (e.currentTarget as HTMLDivElement).style.boxShadow = '0 12px 40px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLDivElement).style.transform = '';
                    (e.currentTarget as HTMLDivElement).style.boxShadow = '';
                  }}
                >
                  <div className="building-card-img" style={{ background:gradient, position:'relative', overflow:'hidden' }}>
                    {cardImgUrl ? (
                      <img src={cardImgUrl} alt={b.name} style={{ width:'100%', height:'100%', objectFit:'cover', position:'absolute', inset:0, display:'block' }}/>
                    ) : b.mainImageFsId ? (
                      <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center' }}>
                        <div style={{ width:24, height:24, border:'2px solid rgba(255,255,255,0.3)', borderTopColor:'#fff', borderRadius:'50%', animation:'spin 0.8s linear infinite' }}/>
                      </div>
                    ) : (
                      <Building2 size={56} color="rgba(255,255,255,0.35)"/>
                    )}
                    <span className={`building-status status-${(b.status||'').toLowerCase()}`}>
                      {b.status==='COMPLETED'?'Completed':b.status==='AVAILABLE'?'Available':'Sublet'}
                    </span>
                    {totalImgs > 0 && (
                      <span style={{ position:'absolute', bottom:8, right:8, background:'rgba(0,0,0,0.5)', color:'#fff', fontSize:'0.7rem', fontWeight:600, padding:'2px 8px', borderRadius:10, display:'flex', alignItems:'center', gap:4 }}>
                        <ImageIcon size={11}/> {totalImgs}
                      </span>
                    )}
                  </div>
                  <div className="building-card-body">
                    <div className="building-card-type">{b.type}</div>
                    <div className="building-card-name">{b.name}</div>
                    <div className="building-card-location"><MapPin size={13}/>{b.location}</div>
                    <p style={{ fontSize:'0.83rem', color:'var(--text-muted)', lineHeight:'1.6', marginBottom:'12px' }}>
                      {(b.description?.length??0) > 100 ? b.description.slice(0,100)+'…' : b.description}
                    </p>
                    <div className="building-card-meta">
                      <div className="building-meta-item"><Maximize2 size={13}/>{b.area}</div>
                      <div className="building-meta-item"><Calendar size={13}/>{b.completedYear}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Pagination ── */}
        {filtered.length > 0 && (
          <>
            <Pagination
              page={safePage}
              totalPages={totalPages}
              onPrev={() => setPage(p => Math.max(1, p - 1))}
              onNext={() => setPage(p => Math.min(totalPages, p + 1))}
              onPage={setPage}
            />
            {/* Page info */}
            <p style={{ textAlign:'center', marginTop:12, fontSize:'0.78rem', color:'var(--text-muted)' }}>
              Showing {pageStart + 1}–{Math.min(pageStart + perPage, filtered.length)} of {filtered.length} building{filtered.length!==1?'s':''}
            </p>
          </>
        )}
      </div>

      {selected && <BuildingDetailModal building={selected} onClose={() => setSelected(null)}/>}
    </section>
  );
};

export default BuildingCatalogue;